/**
 * Card 1 state derivation for design_handoff_billing. Centralises which
 * badge/band/facts/actions a given org falls into, so PlanSummaryCard.jsx
 * stays pure rendering. Precedence (first match wins) mirrors how the old
 * OrganizationSubscription.js prioritised is_trial > is_terminated > active,
 * extended with the free/bundle/pending/reverted/renewal-failed branches
 * this handoff adds.
 */
import { PLANS } from "../../../../consts";
import { getRevertedPlan, wasCancelledVoluntarily } from "../../helpers/subscription";

const dateFormat = Intl.DateTimeFormat(window.navigator.language || "en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

const fmt = (unixSeconds) =>
  unixSeconds ? dateFormat.format(new Date(unixSeconds * 1000)) : null;

const money = (amount, currency = "EUR") =>
  new Intl.NumberFormat(window.navigator.language || "en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);

/**
 * @param {Object} currentOrganization - useCurrentOrganizationStore().currentOrganization
 * @param {boolean=} checkingOut - true right after returning from Stripe
 *   Checkout, before the confirming webhook has landed. The DB's own
 *   pending_payment flag only gets set for the "requires action" (e.g. 3DS)
 *   case — a plain trial/active activation never sets it — so this covers
 *   the gap for every other checkout outcome.
 * @param {boolean=} accountHasUsedTrial - useUserStore().user.model.has_used_trial;
 *   true if this account already started a trial on ANY workspace it
 *   created (one trial per account, not per workspace — see
 *   OrganizationsTable::hasCreatorUsedTrialElsewhere on the backend).
 * @returns {Object} { badge, band, title, subtitle, facts, primaryActions, footerLinks }
 */
export function getPlanSummaryState(
  currentOrganization,
  checkingOut = false,
  accountHasUsedTrial = false
) {
  const organizationModel = currentOrganization.model;
  const currentSubscription = currentOrganization.subscription || {};
  const billingEmail = organizationModel.treasurer_email;
  const pendingPayment =
    Boolean(organizationModel.pending_payment * 1) || checkingOut;
  const renewalFailed = Boolean(
    organizationModel.organization_settings
      ?.sent_timestamp_renewal_failed_period_1
  );
  // Either this specific workspace already had its own trial, or this
  // account already used its one trial elsewhere — both stop the backend
  // from granting another one here, so both need to flip this to true.
  const hasUsedTrial =
    accountHasUsedTrial ||
    (organizationModel.organizations_plans || []).some((p) => p.is_trial * 1);
  const hasBillingHistory = (organizationModel.organizations_plans || []).some(
    (p) => p.stripe_subscription_id
  );
  const revertedPlan = getRevertedPlan(currentOrganization);
  const voluntarilyCancelled = wasCancelledVoluntarily(currentOrganization);

  const billingEmailFact = billingEmail
    ? {
        label: "Billing email",
        value: billingEmail,
        action: "editBillingEmail",
      }
    : null;

  // 08 — Bundle: not self-serve, billed outside this workspace.
  if (currentSubscription.is_bundle) {
    return {
      badge: { color: "success", label: "Active" },
      title: currentSubscription.name || "Bundle plan",
      subtitle: "Yearly renewal · billed through the bundle",
      band: {
        kind: "info",
        icon: "Package",
        title: `This workspace is part of the ${
          currentSubscription.name || "bundle"
        }.`,
        body: "Your plan and limits are set by the bundle owner, and billing is handled outside this workspace. Changing plan here is not possible.",
      },
      facts: [
        currentSubscription.expiry && {
          label: "Valid until",
          value: fmt(currentSubscription.expiry),
        },
      ].filter(Boolean),
      primaryActions: [
        {
          key: "changePlan",
          label: "Change plan",
          hierarchy: "primary",
          disabled: true,
        },
      ],
      footerLinks: [],
      footerNote:
        "Payments are handled by the bundle owner and are not visible here.",
    };
  }

  // 16 — Payment processing: returned from checkout, webhook hasn't landed.
  if (pendingPayment) {
    return {
      badge: { color: "gray", label: "Pending" },
      title: currentSubscription.name || "Subscription",
      subtitle: "Your plan will become active shortly",
      band: {
        kind: "info",
        icon: "ClockRewind",
        title: "Your subscription payment is being processed",
        body: "Your plan will become active shortly. This page updates on its own once it's confirmed — there is nothing to do, and reloading will not charge you twice.",
      },
      facts: [
        { label: "Paid", value: "Today, via Stripe" },
        { label: "Waiting on", value: "Confirmation from Stripe" },
        billingEmailFact,
      ].filter(Boolean),
      primaryActions: [
        {
          key: "changePlan",
          label: "Change plan",
          hierarchy: "primary",
          disabled: true,
        },
      ],
      footerLinks: [],
      footerNote: "Payment history appears once the payment is confirmed.",
    };
  }

  const isFree =
    !currentSubscription.code || currentSubscription.code === PLANS.free;

  // 06 — Reverted to free after an involuntary lapse (failed renewal).
  // `organizations_plans` rows don't carry a resolvable plan name (see
  // getRevertedPlan's comment), so the copy stays generic ("your previous
  // plan") rather than interpolating a name it can't actually get.
  if (isFree && revertedPlan) {
    return {
      badge: { color: "error", label: "Reverted" },
      title: "Forever Free plan",
      subtitle: `Your previous plan ended on ${fmt(revertedPlan.expiry)}.`,
      band: {
        kind: "err",
        icon: "AlertCircle",
        title: "Renewal failed, so the plan reverted to free",
        body: "Some widgets, website domains or team members may now be blocked while your usage is above the free plan's limits. Renewing restores everything exactly as it was — nothing was deleted.",
      },
      facts: [
        revertedPlan.price && {
          label: "Previous plan",
          value: `${money(revertedPlan.price)} per ${
            revertedPlan.is_annual * 1 ? "year" : "month"
          }`,
        },
        {
          label: "Reverted on",
          value: fmt(currentSubscription.created || revertedPlan.expiry),
        },
        billingEmailFact,
      ].filter(Boolean),
      primaryActions: [
        {
          key: "renew",
          label: "Renew plan",
          hierarchy: "primary",
          // external: true,
        },
      ],
      footerLinks: hasBillingHistory
        ? [{ key: "paymentHistory", label: "Payment history", external: true }]
        : [],
    };
  }

  // 01 / 02 — Free, never trialled / trial already used.
  if (isFree) {
    // A plain, no-band Free state for a workspace whose most recent paid
    // plan was cancelled on purpose — "your free trial has already been
    // used" would be misleading here; they didn't just run out a trial,
    // they had a real subscription and chose to end it.
    if (voluntarilyCancelled) {
      return {
        badge: { color: "gray", label: "Free" },
        title: "Forever Free plan",
        subtitle: "Valid indefinitely. No subscription, so nothing to renew.",
        band: null,
        facts: [billingEmailFact].filter(Boolean),
        primaryActions: [
          {
            key: "changePlan",
            label: hasUsedTrial ? "Upgrade" : "Start 30-day free trial",
            hierarchy: "primary",
          },
        ],
        footerLinks: hasBillingHistory
          ? [{ key: "paymentHistory", label: "Payment history", external: true }]
          : [],
        footerNote: hasBillingHistory ? null : "No payments yet.",
      };
    }

    return hasUsedTrial
      ? {
          badge: { color: "gray", label: "Free" },
          title: "Forever Free plan",
          subtitle: "Valid indefinitely. No subscription, so nothing to renew.",
          band: {
            kind: "gray",
            icon: "ClockRewind",
            title: "Your free trial has already been used",
            body: "A trial is available once per workspace, so upgrading now starts a paid plan straight away.",
          },
          facts: [
            { label: "Renews", value: "Never — the free plan does not expire" },
            billingEmailFact,
          ].filter(Boolean),
          primaryActions: [
            { key: "changePlan", label: "Upgrade", hierarchy: "primary" },
          ],
          footerLinks: hasBillingHistory
            ? [
                {
                  key: "paymentHistory",
                  label: "Payment history",
                  external: true,
                },
              ]
            : [],
          footerNote: hasBillingHistory ? null : "No payments yet.",
        }
      : {
          badge: { color: "gray", label: "Free" },
          title: "Forever Free plan",
          subtitle: "Valid indefinitely. No subscription, so nothing to renew.",
          band: null,
          facts: [
            // { label: "Renews", value: "Never — the free plan does not expire" },
            billingEmailFact,
          ].filter(Boolean),
          primaryActions: [
            {
              key: "changePlan",
              label: "Start 30-day free trial",
              hierarchy: "primary",
            },
          ],
          footerLinks: [],
          footerNote: "No payments yet.",
        };
  }

  // 05 — Cancelled: still usable until the paid period ends. Checked before
  // Trial below — cancelling mid-trial leaves is_trial=1 (Stripe status
  // stays "trialing" until the trial actually ends) alongside is_terminated=1,
  // and this needs to win so a cancelled trial doesn't keep showing "First
  // charge" for a charge that will no longer happen.
  if (currentSubscription.is_terminated * 1) {
    return {
      badge: { color: "warning", label: "Cancelled" },
      title: currentSubscription.name,
      subtitle: "Will not renew",
      band: {
        kind: "warn",
        icon: "SlashCircle01",
        title: `Your subscription ends on ${fmt(currentSubscription.expiry)}`,
        body: "Everything keeps working until then. After that the workspace reverts to the free plan, and anything over the free limits stops being published.",
      },
      facts: [
        { label: "Access until", value: fmt(currentSubscription.expiry) },
        { label: "Renews", value: "No — cancelled" },
        billingEmailFact,
      ].filter(Boolean),
      primaryActions: [
        {
          key: "renew",
          label: "Renew plan",
          hierarchy: "primary",
          external: true,
        },
        { key: "changePlan", label: "Change plan", hierarchy: "secondaryGray" },
      ],
      footerLinks: hasBillingHistory
        ? [{ key: "paymentHistory", label: "Payment history", external: true }]
        : [],
    };
  }

  // 04 — Trial: card already on file, band states the charge, not a request.
  if (currentSubscription.is_trial * 1) {
    return {
      badge: { color: "gray", label: "Trial" },
      title: currentSubscription.name,
      subtitle: `Monthly renewal after the trial${
        currentSubscription.price
          ? ` · ${money(currentSubscription.price)} per month`
          : ""
      }`,
      band: null /*{
        kind: "info",
        icon: "ClockRewind",
        title: `Your trial ends on ${fmt(currentSubscription.expiry)}`,
        body: `Your card is already on file with Stripe.${
          currentSubscription.price
            ? ` ${money(
                currentSubscription.price
              )} is charged on that date unless you cancel first.`
            : " It's charged on that date unless you cancel first."
        }`,
      }*/,
      facts: [
        { label: "Trial ends", value: fmt(currentSubscription.expiry) },
        currentSubscription.price && {
          label: "First charge",
          value: `${money(currentSubscription.price)} on ${fmt(
            currentSubscription.expiry
          )}`,
        },
        billingEmailFact,
      ].filter(Boolean),
      primaryActions: [
        { key: "changePlan", label: "Change plan", hierarchy: "primary" },
        {
          key: "manageSubscription",
          label: "Manage subscription",
          hierarchy: "secondaryGray",
          external: true,
        },
      ],
      footerLinks: [
        { key: "cancelTrial", label: "Cancel trial" },
        hasBillingHistory && {
          key: "paymentHistory",
          label: "Payment history",
          external: true,
        },
      ].filter(Boolean),
    };
  }

  // 07 — Renewal failed: stacked on the otherwise-active state.
  if (renewalFailed) {
    return {
      badge: { color: "warning", label: "Renewal failed" },
      title: currentSubscription.name,
      subtitle: `${
        currentSubscription.is_annual * 1 ? "Yearly" : "Monthly"
      } renewal${
        currentSubscription.price
          ? ` · ${money(currentSubscription.price)} per ${
              currentSubscription.is_annual * 1 ? "year" : "month"
            }`
          : ""
      }`,
      band: {
        kind: "err",
        icon: "CreditCardDown",
        title: "Renewal payment failed",
        body: "We could not charge the card on file. Update it in the billing portal, or the plan will revert to free if payment keeps failing.",
      },
      facts: [
        currentSubscription.price && {
          label: "Amount due",
          value: money(currentSubscription.price),
        },
        billingEmailFact,
      ].filter(Boolean),
      primaryActions: [
        {
          key: "updateCard",
          label: "Update card",
          hierarchy: "primary",
          external: true,
        },
        { key: "changePlan", label: "Change plan", hierarchy: "secondaryGray" },
      ],
      footerLinks: [
        { key: "cancelSubscription", label: "Cancel subscription" },
        hasBillingHistory && {
          key: "paymentHistory",
          label: "Payment history",
          external: true,
        },
      ].filter(Boolean),
    };
  }

  // 03 — Active paid, the common case: no band at all.
  return {
    badge: { color: "success", label: "Active" },
    title: currentSubscription.name,
    subtitle: `${
      currentSubscription.is_annual * 1 ? "Yearly" : "Monthly"
    } renewal${
      currentSubscription.price
        ? ` · ${money(currentSubscription.price)} per ${
            currentSubscription.is_annual * 1 ? "year" : "month"
          }`
        : ""
    }`,
    band: null,
    facts: [
      { label: "Valid until", value: fmt(currentSubscription.expiry) },
      {
        label: "Renews",
        value: `Automatically on ${fmt(currentSubscription.expiry)}`,
      },
      billingEmailFact,
    ].filter(Boolean),
    primaryActions: [
      { key: "changePlan", label: "Change plan", hierarchy: "primary" },
      {
        key: "manageSubscription",
        label: "Manage subscription",
        hierarchy: "secondaryGray",
        external: true,
      },
    ],
    footerLinks: [
      { key: "cancelSubscription", label: "Cancel subscription" },
      hasBillingHistory && {
        key: "paymentHistory",
        label: "Payment history",
        external: true,
      },
    ].filter(Boolean),
  };
}
